import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Smile } from 'lucide-react'
import type { KpiTrends } from '@/types/analytics'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { monthLabel } from './chart-utils'

interface SatisfactionChartProps {
  trends: KpiTrends | undefined
  isLoading: boolean
}

// Customer satisfaction: CSI (left axis, 0–100) and NPS (right axis,
// −100–100) by month.
export function SatisfactionChart({ trends, isLoading }: SatisfactionChartProps) {
  const chartData =
    trends?.satisfaction.map((p) => ({
      month: monthLabel(p.month),
      csi: p.csi,
      nps: p.nps,
    })) ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Smile className="h-4 w-4" /> Customer Satisfaction
        </CardTitle>
        <CardDescription>CSI target ≥ 90%, NPS target 50+</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : chartData.length === 0 ? (
          <div className="text-muted-foreground flex h-[200px] items-center justify-center text-sm">
            No survey data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="csi"
                domain={[0, 100]}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `${v}%`}
              />
              <YAxis
                yAxisId="nps"
                orientation="right"
                domain={[-100, 100]}
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip {...CHART_TOOLTIP_PROPS} />
              <Legend
                verticalAlign="bottom"
                iconType="plainline"
                iconSize={12}
                formatter={(value: string) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
              <ReferenceLine
                yAxisId="csi"
                y={90}
                stroke="hsl(var(--success))"
                strokeDasharray="4 4"
                label={{
                  value: 'CSI 90%',
                  position: 'insideTopLeft',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
              <ReferenceLine
                yAxisId="nps"
                y={50}
                stroke="hsl(var(--chart-2))"
                strokeDasharray="4 4"
              />
              <Line
                yAxisId="csi"
                type="monotone"
                dataKey="csi"
                name="CSI %"
                stroke="hsl(var(--chart-1))"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="nps"
                type="monotone"
                dataKey="nps"
                name="NPS"
                stroke="hsl(var(--chart-4))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
